import { Link } from "@tanstack/react-router";
import { splitEnglishBrands } from "@/lib/english-brands";
import { Phone, MapPin, Mail, Clock } from "lucide-react";
import { XSocialIcon } from "@/components/site/XSocialIcon";
import {
  FacebookIcon,
  InstagramIcon,
  YoutubeIcon,
  LinkedinIcon,
} from "@/components/site/SocialBrandIcons";
import {
  SITE_ADDRESS,
  SITE_MAPS_URL,
  SITE_EMAIL,
  SITE_PHONE,
  SITE_PHONE_TEL,
  SITE_SOCIAL,
} from "@/lib/site-info";
import { m } from "@/paraglide/messages.js";

export function SiteFooter() {
  return (
    <footer className="border-t border-white/10 bg-black text-foreground/75">
      <div className="container-page grid gap-10 py-16 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div
            className="inline-flex bg-ember-fill px-3 py-1.5 font-display text-lg leading-none tracking-wider text-foreground"
            style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%)" }}
          >
            {m.footer_brand()}
          </div>
          <p className="mt-5 max-w-md text-sm leading-relaxed">{m.footer_blurb()}</p>
          <div className="mt-6 flex items-center gap-3 text-foreground/65">
            <a
              href={SITE_SOCIAL.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="hover:text-ember"
            >
              <FacebookIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE_SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-ember"
            >
              <InstagramIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE_SOCIAL.youtube}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="hover:text-ember"
            >
              <YoutubeIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE_SOCIAL.twitter}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="hover:text-ember"
            >
              <XSocialIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE_SOCIAL.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="hover:text-ember"
            >
              <LinkedinIcon className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <p className="font-display text-sm tracking-[0.18em] text-foreground">
            {m.footer_explore()}
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <Link to="/method" className="hover:text-ember">
                {m.footer_link_method()}
              </Link>
            </li>
            <li>
              <Link to="/play-injury-free" className="hover:text-ember">
                {m.footer_link_injury_free()}
              </Link>
            </li>
            <li>
              <Link to="/lessons" className="hover:text-ember">
                {m.footer_link_lessons()}
              </Link>
            </li>
            <li>
              <Link to="/practice" className="hover:text-ember">
                {m.nav_practice_hub()}
              </Link>
            </li>
            <li>
              <Link to="/prices" className="hover:text-ember">
                {m.footer_link_prices()}
              </Link>
            </li>
            <li>
              <Link to="/range" className="hover:text-ember">
                {m.footer_link_range()}
              </Link>
            </li>
            <li>
              <Link to="/hall-of-fame" className="hover:text-ember">
                {m.footer_link_hof()}
              </Link>
            </li>
            <li>
              <Link to="/blog" className="hover:text-ember">
                {m.footer_link_blog()}
              </Link>
            </li>
            <li>
              <Link to="/events" className="hover:text-ember">
                {m.footer_link_events()}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-display text-sm tracking-[0.18em] text-foreground">
            {m.footer_partners()}
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a
                href="https://markeglyfoundation.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ember"
              >
                Mark Egly Foundation
              </a>
            </li>
            <li>
              <a
                href="https://alpha1life.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ember"
              >
                Alpha1 Life
              </a>
            </li>
            <li>
              <a
                href="https://fairunderwriting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ember"
              >
                Fair Underwriting
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-display text-sm tracking-[0.18em] text-foreground">
            {m.footer_get_in_touch()}
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-ember" />
              <a href={SITE_PHONE_TEL} className="hover:text-ember">
                {SITE_PHONE}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-ember" />
              <a href={`mailto:${SITE_EMAIL}`} className="hover:text-ember">
                {SITE_EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 text-ember" />
              <a href={SITE_MAPS_URL} className="hover:text-ember">
                <bdi dir="ltr">{SITE_ADDRESS.full}</bdi>
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 text-ember" />
              <span>{m.site_hours()}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page flex flex-col items-center justify-between gap-3 py-5 text-xs uppercase tracking-[0.18em] text-muted-foreground sm:flex-row">
          <span>{m.footer_copyright({ year: String(new Date().getFullYear()) })}</span>
          <span>
            {splitEnglishBrands(m.footer_tagline()).map((part, i) =>
              part.brand ? (
                <span key={i} lang="en">
                  {part.text}
                </span>
              ) : (
                part.text
              ),
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}
